import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Code as CodeIcon, Copy, LoaderCircle } from 'lucide-react';
import { Button } from '../Button.jsx';
import { COPY_RESET_MS } from '../copy.jsx';
import { highlightToLines } from './highlight.js';

const QUERY = '(prefers-reduced-motion: reduce)';

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(() => typeof matchMedia === 'function' && matchMedia(QUERY).matches);
  useEffect(() => {
    const mq = matchMedia(QUERY);
    const onChange = () => setReduce(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduce;
}

/**
 * Syntax-highlighted code surface. Rows are keyed by their offset in the
 * source, so a line that is already on screen stays put while more code
 * streams in. While `status` is `streaming` the bar says so and the viewport
 * follows the newest line; `complete` is unmarked and stays where the reader
 * scrolled.
 *
 * `bar={false}` is Storybook's Source: no header, the copy button in the
 * corner. `copy` is the older name for `copyable`. `title` follows the file
 * icon in the bar and `tools` follow the copy button, so the bar can head a
 * panel section.
 */
export function CodeBlock({
  code = '',
  language = 'html',
  filename,
  status = 'complete',
  showLineNumbers = true,
  highlightLines = [],
  maxHeight,
  wrap = false,
  copyable = true,
  copy,
  onCopy,
  bar = true,
  title,
  tools,
  className = '',
}) {
  const reduce = usePrefersReducedMotion();
  const viewportRef = useRef(null);
  const copyTimer = useRef(undefined);
  const [copied, setCopied] = useState(false);
  const streaming = status === 'streaming';
  const source = String(code).replace(/\r\n/g, '\n');
  const showCopy = (copy ?? copyable) !== false || Boolean(onCopy);
  const highlighted = useMemo(() => new Set(highlightLines), [highlightLines]);
  const htmlLines = useMemo(() => highlightToLines(source, language), [source, language]);
  const rows = useMemo(() => {
    let offset = 0;
    return source.split('\n').map((content, index) => {
      const row = { offset, html: htmlLines[index] ?? '' };
      offset += content.length + 1;
      return row;
    });
  }, [source, htmlLines]);

  useEffect(() => () => {
    if (copyTimer.current) window.clearTimeout(copyTimer.current);
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !streaming) return undefined;
    const frame = requestAnimationFrame(() => {
      if (viewport.scrollHeight <= viewport.clientHeight) return;
      viewport.scrollTo({ top: viewport.scrollHeight, behavior: reduce ? 'auto' : 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [source, streaming, reduce]);

  const handleCopy = useCallback(async () => {
    try {
      if (onCopy) await onCopy();
      else await navigator.clipboard.writeText(source);
      setCopied(true);
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(false), COPY_RESET_MS);
    } catch {
      // Clipboard blocked, or onCopy rejected: leave the button as it was.
    }
  }, [onCopy, source]);

  const langLabel = language && language !== 'text' ? language : '';
  const copyButton = showCopy ? (
    <Button
      variant="ghost"
      size="compact"
      leadingIcon={copied ? Check : Copy}
      className={bar ? undefined : 'edl-code__copy'}
      aria-label={copied ? 'Copied' : 'Copy code'}
      tooltip={copied ? 'Copied' : 'Copy code'}
      onClick={handleCopy}
    />
  ) : null;

  return (
    <div
      data-state={status}
      data-lines={showLineNumbers ? 'on' : 'off'}
      data-wrap={wrap ? '' : undefined}
      aria-busy={streaming || undefined}
      className={['edl-code', bar ? '' : 'edl-code--bare', className].filter(Boolean).join(' ')}
      style={maxHeight == null ? undefined : { '--edl-code-max': `${maxHeight}px` }}
    >
      {bar && (
        <div className="edl-code__bar">
          <CodeIcon className="edl-code__file" size={14} strokeWidth={1.5} aria-hidden="true" />
          {title}
          {filename ? <span className="edl-code__name">{filename}</span> : null}
          {langLabel ? <span className="edl-code__lang">{langLabel}</span> : null}
          {(streaming || copyButton || tools) && (
            <span className="edl-code__tools">
              {streaming ? (
                <span className="edl-code__status">
                  <span className="edl-code__mark" aria-hidden="true">
                    <LoaderCircle className="edl-code__spin" size={12} strokeWidth={1.5} />
                  </span>
                  Writing
                </span>
              ) : null}
              {copyButton}
              {tools}
            </span>
          )}
        </div>
      )}
      <div
        ref={viewportRef}
        className="edl-code__view edl-scroll-fade"
        role={streaming ? 'log' : undefined}
        aria-live={streaming ? 'polite' : undefined}
        aria-relevant={streaming ? 'additions' : undefined}
      >
        <pre className="edl-code__pre">
          <code>
            {rows.map((row, index) => {
              const lineNumber = index + 1;
              return (
                <span key={row.offset} className={highlighted.has(lineNumber) ? 'edl-code__line is-focus' : 'edl-code__line'}>
                  {showLineNumbers ? <span className="edl-code__no" aria-hidden="true">{lineNumber}</span> : null}
                  <span className="edl-code__text" dangerouslySetInnerHTML={{ __html: row.html }} />
                </span>
              );
            })}
          </code>
        </pre>
      </div>
      {!bar && copyButton}
    </div>
  );
}

export const Code = CodeBlock;
